import React from 'react';
import { Alert, Table } from 'react-bootstrap';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
  Line,
} from 'recharts';

import StudentResults from '../../../types/StudentResults';
import { average, standardDeviation } from '../../../lib/statistics';

interface StudentsDiagramProps {
  studentsResults: StudentResults[];
}

const StudentsDiagram = ({ studentsResults }: StudentsDiagramProps) => {
  const getCount = (key: keyof StudentResults, value: number) =>
    studentsResults.filter((result) => result[key] === value).length;

  const riskFactors = [
    { label: 'ППЗ', key: 'stansPPZ' as keyof StudentResults },
    { label: 'ПВГ', key: 'stansPVG' as keyof StudentResults },
    { label: 'ПАУ', key: 'stansPAU' as keyof StudentResults },
    { label: 'СР', key: 'stansSR' as keyof StudentResults },
    { label: 'ИМ', key: 'stansIM' as keyof StudentResults },
    { label: 'ТР', key: 'stansTR' as keyof StudentResults },
    { label: 'ФР', key: 'stansFR' as keyof StudentResults },
    { label: 'ДЕ', key: 'stansDE' as keyof StudentResults },
  ];

  const protectionFactors = [
    { label: 'ПР', key: 'stansPR' as keyof StudentResults },
    { label: 'ПО', key: 'stansPO' as keyof StudentResults },
    { label: 'СА', key: 'stansSA' as keyof StudentResults },
    { label: 'СП', key: 'stansSP' as keyof StudentResults },
    { label: 'СЭ', key: 'stansSE' as keyof StudentResults },
    { label: 'АН', key: 'stansAN' as keyof StudentResults },
    { label: 'ФУ', key: 'stansFU' as keyof StudentResults },
    { label: 'ДО', key: 'stansDO' as keyof StudentResults },
  ];

  const selectNonzeroResults = (key: keyof StudentResults) => studentsResults
    .map((result) => result[key])
    .filter((value) => value !== 0);

  const riskFactorsAverage = Object.fromEntries(
    riskFactors.map(({ key }) => 
      [key, Math.round((average(selectNonzeroResults(key)) + Number.EPSILON) * 100) / 100])
  );

  const riskFactorsAveragePlusSD = Object.fromEntries(
    riskFactors.map(({ key }) => {
      const av = riskFactorsAverage[key];
      const sd = standardDeviation(selectNonzeroResults(key), av);
      return [key, Math.round((av + sd + Number.EPSILON) * 100) / 100];
    })
  );

  const riskFactorsAverageMinusSD = Object.fromEntries(
    riskFactors.map(({ key }) => {
      const av = riskFactorsAverage[key];
      const sd = standardDeviation(selectNonzeroResults(key), av);
      return [key, Math.round((av - sd + Number.EPSILON) * 100) / 100];
    })
  );

  const protectionFactorsAverage = Object.fromEntries(
    protectionFactors.map(({ key }) => 
      [key, Math.round((average(selectNonzeroResults(key)) + Number.EPSILON) * 100) / 100])
  );

  const protectionFactorsAveragePlusSD = Object.fromEntries(
    protectionFactors.map(({ key }) => {
      const av = protectionFactorsAverage[key];
      const sd = standardDeviation(selectNonzeroResults(key), av);
      return [key, Math.round((av + sd + Number.EPSILON) * 100) / 100];
    })
  );

  const protectionFactorsAverageMinusSD = Object.fromEntries(
    protectionFactors.map(({ key }) => {
      const av = protectionFactorsAverage[key];
      const sd = standardDeviation(selectNonzeroResults(key), av);
      return [key, Math.round((av - sd + Number.EPSILON) * 100) / 100];
    })
  );

  return (
    <section className="d-flex flex-column flex-wrap gap-4 justify-content-stretch">
      <Alert variant="warning w-100 text-center mb-4">
        Внимание! Статистика отображается только по ученикам, прошедшим
        тестирование
      </Alert>

      <p className="w-100 mb-0 mt-3">
        Всего прошло тест: {studentsResults.length} уч.
      </p>

      <h4 className="mb-4 mt-4">Факторы риска (стэны)</h4>

      <LineChart
        width={500}
        height={300}
        data={riskFactors.map(({ label, key }) => ({
          name: label,
          "M": riskFactorsAverage[key],
          "M + SD": riskFactorsAveragePlusSD[key],
          "M - SD": riskFactorsAverageMinusSD[key],
        }))}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis allowDecimals={false} />
        <Tooltip />

        <Line dataKey="M" stroke="#ffd300" />
        <Line dataKey="M + SD" stroke="#1e90ff" />
        <Line dataKey="M - SD" stroke="#1e90ff" />
      </LineChart>

      <Table>
        <thead>
          <tr>
            <td/>
            {riskFactors.map(({ label, key }) => (
              <td key={`rthead-${key}`}>{label}</td>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>M</td>
            {riskFactors.map(({ key }) => (
              <td key={`rtmrow-${key}`}>{riskFactorsAverage[key]}</td>
            ))}
          </tr>

          <tr>
            <td>M + SD</td>
            {riskFactors.map(({ key }) => (
              <td key={`rtmpsdrow-${key}`}>{riskFactorsAveragePlusSD[key]}</td>
            ))}
          </tr>

          <tr>
            <td>M - SD</td>
            {riskFactors.map(({ key }) => (
              <td key={`rtmmsdrow-${key}`}>{riskFactorsAverageMinusSD[key]}</td>
            ))}
          </tr>
        </tbody>
      </Table>

      {riskFactors.map(({ label, key }) => (
        <React.Fragment key={key}>
          <h6>{label}</h6>

          <BarChart
            width={500}
            height={300}
            data={Array.from(Array(10).keys()).map((i) => ({
              name: i + 1,
              val: getCount(key, i + 1),
            }))}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis allowDecimals={false} />
            <Tooltip />

            <Bar name="Количество" dataKey="val">
              {Array.from(Array(10).keys()).map((i) => (
                <Cell
                  key={`cell-${i}`}
                  fill={i >= 3 && i <= 6 ? 'limegreen' : 'tomato'}
                />
              ))}
            </Bar>
          </BarChart>
        </React.Fragment>
      ))}

      <h4 className="mb-4 mt-4">Факторы защиты (стэны)</h4>

      <LineChart
        width={500}
        height={300}
        data={protectionFactors.map(({ label, key }) => ({
          name: label,
          "M": protectionFactorsAverage[key],
          "M + SD": protectionFactorsAveragePlusSD[key],
          "M - SD": protectionFactorsAverageMinusSD[key],
        }))}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis allowDecimals={false} />
        <Tooltip />

        <Line dataKey="M" stroke="#ffd300" />
        <Line dataKey="M + SD" stroke="#1e90ff" />
        <Line dataKey="M - SD" stroke="#1e90ff" />
      </LineChart>

      <Table>
        <thead>
          <tr>
            <td/>
            {protectionFactors.map(({ label, key }) => (
              <td key={`pthead-${key}`}>{label}</td>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>M</td>
            {protectionFactors.map(({ key }) => (
              <td key={`ptmrow-${key}`}>{protectionFactorsAverage[key]}</td>
            ))}
          </tr>

          <tr>
            <td>M + SD</td>
            {protectionFactors.map(({ key }) => (
              <td key={`ptmpsdrow-${key}`}>{protectionFactorsAveragePlusSD[key]}</td>
            ))}
          </tr>

          <tr>
            <td>M - SD</td>
            {protectionFactors.map(({ key }) => (
              <td key={`ptmmsdrow-${key}`}>{protectionFactorsAverageMinusSD[key]}</td>
            ))}
          </tr>
        </tbody>
      </Table>

      {protectionFactors.map(({ label, key }) => (
        <React.Fragment key={key}>
          <h6>{label}</h6>

          <BarChart
            width={500}
            height={300}
            data={Array.from(Array(10).keys()).map((i) => ({
              name: i + 1,
              val: getCount(key, i + 1),
            }))}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis allowDecimals={false} />
            <Tooltip />

            <Bar name="Количество" dataKey="val">
              {Array.from(Array(10).keys()).map((i) => (
                <Cell
                  key={`cell-${i}`}
                  fill={i >= 3 && i <= 6 ? 'limegreen' : 'tomato'}
                />
              ))}
            </Bar>
          </BarChart>
        </React.Fragment>
      ))}

      <h4 className="mb-4 mt-4">Вероятность проявления рискового поведения</h4>

      <BarChart
        width={500}
        height={300}
        data={[
          { name: 'Низкая', val: getCount('result', 0) },
          { name: 'Высокая', val: getCount('result', 1) },
          { name: 'Высочайшая', val: getCount('result', 2) },
        ]}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis allowDecimals={false} />
        <Tooltip />

        <Bar name="Количество" dataKey="val">
          <Cell fill="limegreen" />
          <Cell fill="#fff047" />
          <Cell fill="tomato" />
        </Bar>
      </BarChart>
    </section>
  );
};

export default StudentsDiagram;
